import {CSSObject} from '@emotion/react'
import styled from '@emotion/styled'
// import {useLocation} from '@reach/router'

import {useEffect} from 'react'

import Header from 'src/components/Header'
import Footer from 'src/components/Footer'
import {Box, Flex, PositionContainer, BoxProps} from 'src/components/Box'
import {zIndexes} from 'src/styles/zIndexes'
import ToastMessages from 'src/components/ToastMessages'
import {vhHeight} from 'src/styles'
import * as breakpoints from 'src/styles/mediaQuery'
import {Theme} from 'src/styles/theme'
import {isLmsImplementation} from 'src/lib/util'
import {useResumeEvent} from 'src/hooks/useResumeEvent'

import {LoadingContentPlaceholder} from './Loading'

interface Props {
  footerMargin?: BoxProps['mt']
  hideFooter?: boolean
  hideHeaderLinks?: boolean
  narrow?: boolean
  loadingContent?: boolean
  children?: React.ReactNode | React.ReactNode[]
  overflow?: string
}

// NOTE: To maintain some margin at large sizes, make sure page layout's max width is smaller than the large break point.
const maxWidth = (props: {theme: Theme} & Props): CSSObject => ({
  maxWidth: props.narrow
    ? '675px'
    : `calc(${props.theme.breakpoints.lg} - ${props.theme.space[7]})`,
})

const MainContainer = styled(Flex)<Props>(
  ({theme}) => ({
    boxSizing: 'border-box',
    margin: '0 auto',
    paddingLeft: theme.space[3],
    paddingRight: theme.space[3],
    width: '100%',
    flexDirection: 'column',
    flex: '1 1 auto',
    [breakpoints.large]: {
      paddingLeft: 0,
      paddingRight: 0,
    },
  }),
  maxWidth
)

const ToastContainer = styled(PositionContainer)<Props>(
  {
    position: 'fixed',
    width: '100%',
    zIndex: zIndexes.toast,
    left: '50%',
    transform: 'translateX(-50%)',
  },
  maxWidth
)

const HeaderContainer = styled('div')<Props>({
  position: 'sticky',
  top: 0,
  left: 0,
  right: 0,
  backgroundColor: 'white',
  zIndex: zIndexes.header,
})

const Layout = ({
  children,
  footerMargin = {_: '5', lg: '10'},
  hideFooter = false,
  hideHeaderLinks = false,
  narrow,
  loadingContent = false,
  overflow = 'visible',
}: Props) => {
  const style = loadingContent ? {display: 'none'} : undefined

  const {trackComplete} = useResumeEvent()

  useEffect(() => {
    const startedAt = localStorage.getItem('resume_builder_started_at')
    const completed = localStorage.getItem('resume_builder_completed')

    if (startedAt && !completed) {
      const startTime = new Date(startedAt)
      const now = new Date()
      const minutesSinceStart = (now.getTime() - startTime.getTime()) / 60000

      if (minutesSinceStart > 2) {
        trackComplete()
          .then(() => {
            localStorage.setItem('resume_builder_completed', 'true')
            console.log('Resume builder completion tracked ✅')
          })
          .catch((err) => {
            console.error('Error tracking completion:', err)
          })
      }
    }
  }, [])

  let isLms = true
  isLms = isLmsImplementation()

  return (
    <Flex overflow={overflow} flexDirection="column" minHeight={vhHeight(100)}>
      {!isLms && (
        <HeaderContainer>
          <Header hideNavigationLinks={hideHeaderLinks} />
        </HeaderContainer>
      )}
      <ToastContainer narrow={narrow}>
        <ToastMessages />
      </ToastContainer>
      {loadingContent ? (
        <LoadingContentPlaceholder size="large" mt="9" />
      ) : null}
      <MainContainer style={style} as="main" narrow={narrow}>
        {children}
      </MainContainer>
      {!hideFooter && !isLms && (
        <Box style={style} bg="grey1" as="footer" mt={footerMargin}>
          <MainContainer narrow={false} pt="5" pb="4">
            <Footer />
          </MainContainer>
        </Box>
      )}
    </Flex>
  )
}

export default Layout
